<template>
  <v-card
    flat
    :class="isDark ? 'secondary' : 'primary'"
    dark
    rounded="lg"
  >
    <v-card-title primary-title>
      {{ line }} <span v-if="trial" class="ml-2">(Trial plan)</span>
      <v-spacer></v-spacer>
      <v-chip
        label
        :color="getPlanStatus(status).color"
      >
        {{ $t(`planning.${getPlanStatus(status).text}`) }}
      </v-chip>
    </v-card-title>
    <v-card-text>
      <v-row class="mt-4">
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.part') }}
          </div>
          <div class="title white--text">
            {{ part }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.model') }}
          </div>
          <div class="title white--text">
            {{ model }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.customer') }}
          </div>
          <div class="title white--text">
            {{ customer }}
          </div>
        </v-col>
      </v-row>
      <template v-if="status === 'notStarted'">
        <v-row>
          <v-col cols="4" class="py-0">
            <div>
              {{ $t('assemblyPlanningDover.variant') }}
            </div>
            <div class="title white--text">
              {{ variant }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.scheduledStart') }}
            </div>
            <div class="title white--text">
              {{ scheduledstart }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.scheduledEnd') }}
            </div>
            <div class="title white--text">
              {{ scheduledend }}
            </div>
          </v-col>
        </v-row>
      </template>
      <template v-else-if="status === 'inProgress'">
        <v-row>
          <v-col cols="4" class="py-0">
            <div>
              {{ $t('assemblyPlanningDover.variant') }}
            </div>
            <div class="title white--text">
              {{ variant }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.actualStart') }}
            </div>
            <div class="title white--text">
              {{ actualstart }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.scheduledEnd') }}
            </div>
            <div class="title white--text">
              {{ scheduledend }}
            </div>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row>
          <v-col cols="4" class="py-0">
            <div>
              {{ $t('assemblyPlanningDover.variant') }}
            </div>
            <div class="title white--text">
              {{ variant }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.actualStart') }}
            </div>
            <div class="title white--text">
              {{ actualstart }}
            </div>
          </v-col>
          <v-col cols="4" class="py-0">
            <div class="mt-4">
              {{ $t('assemblyPlanningDover.actualEnd') }}
            </div>
            <div class="title white--text">
              {{ actualend }}
            </div>
          </v-col>
        </v-row>
      </template>
      <v-row class="mt-4">
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.planManHours') }}
          </div>
          <div class="title white--text">
            {{ planCt }} {{ $t('assemblyPlanningDover.hrs') }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.standardManHours') }}
          </div>
          <div class="title white--text">
            {{ standardCt }} {{ $t('assemblyPlanningDover.hrs') }}
          </div>
        </v-col>
        <v-col cols="4" class="py-0">
          <div>
            {{ $t('assemblyPlanningDover.averageCt') }}
          </div>
          <div class="title white--text">
            {{ averageCt }}
          </div>
        </v-col>
        <v-col cols="4">
          <div>
            {{ $t('assemblyPlanningDover.runtime') }}
          </div>
          <div class="title white--text">
            {{ runtime }}
          </div>
        </v-col>
      </v-row>
      <div v-for="(p, n) in selectedPlan" :key="n" class="mt-4">
        <v-progress-linear
          :height="25"
          class="mt-1"
          :color="isDark ? 'primary' : 'secondary'"
          :value="(p.actualquantity / p.plannedquantity) * 100"
        >
          <div
            class="d-inline-block text-truncate font-weight-medium mr-auto ml-2"
            style="max-width: 65%"
            :title="p.partname"
          >
            {{ p.partname }}
          </div>
          <span class="float-right font-weight-medium mr-2">
            {{ p.actualquantity || 0 }}/{{ p.plannedquantity }}
          </span>
        </v-progress-linear>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import { formatDate } from '@shopworx/services/util/date.service';

export default {
  name: 'RejectionPareto',
  inject: ['theme'],
  data() {
    return {
      loading: false,
      standardMatrix: [],
    };
  },
  computed: {
    ...mapState('assemblyPlanningDover', ['selectedPlan']),
    ...mapGetters('assemblyPlanningDover', ['selectedAsset', 'planStatus']),
    ...mapGetters('helper', ['locale']),
    isDark() {
      return this.theme.isDark;
    },
    status() {
      return this.selectedPlan[0].status;
    },
    customer() {
      return this.selectedPlan[0].customer;
    },
    model() {
      return this.selectedPlan[0].model;
    },
    variant() {
      return this.selectedPlan[0].variant;
    },
    trial() {
      return this.selectedPlan[0].trial;
    },
    part() {
      return this.selectedPlan[0].partname;
    },
    line() {
      return this.selectedPlan[0].linename;
    },
    scheduledstart() {
      return this.getFormattedDate(this.selectedPlan[0].scheduledstart);
    },
    scheduledend() {
      return this.getFormattedDate(this.selectedPlan[0].scheduledend);
    },
    actualstart() {
      return this.getFormattedDate(this.selectedPlan[0].actualstart);
    },
    actualend() {
      return this.getFormattedDate(this.selectedPlan[0].actualend);
    },
    standardCt() {
      let sct = this.standardMatrix.reduce((acc, cur) => {
        acc += (+cur.stdcycletime * 1000);
        return acc;
      }, 0);
      if (sct) {
        sct = (sct / 3600000).toFixed(2);
      }
      return sct;
    },
    planCt() {
      let pct = this.selectedPlan[0].machinedetails.reduce((acc, cur) => {
        acc += (+cur.stdcycletime * 1000);
        return acc;
      }, 0);
      if (pct) {
        pct = (pct / 3600000).toFixed(2);
      }
      return pct;
    },
    averageCt() {
      const { averagecycletime } = this.selectedPlan[0];
      if (averagecycletime) {
        const d = Number(averagecycletime / 1000);
        const h = Math.floor(d / 3600);
        const m = Math.floor((d % 3600) / 60);
        const s = Math.floor((d % 3600) % 60);
        return `${h}:${m}:${s}`;
      }
      return '00:00:00';
    },
    runtime() {
      const { runtime } = this.selectedPlan[0];
      if (runtime) {
        const d = Number(runtime / 1000);
        const h = Math.floor(d / 3600);
        const m = Math.floor((d % 3600) / 60);
        const s = Math.floor((d % 3600) % 60);
        return `${h}:${m}:${s}`;
      }
      return '00:00:00';
    },
  },
  async created() {
    this.loading = true;
    const partMatrixList = await this.fetchPartMatrix({ partname: this.part });
    this.standardMatrix = partMatrixList.filter((matrix) => (
      matrix.partname === this.part
      && matrix.linename === this.line
    ));
    if (this.status !== 'notStarted') {
      const reportData = await this.getReportData('assemblyplansummary');
      for (let i = 0; i < this.selectedPlan.length; i += 1) {
        for (let j = 0; j < reportData.length; j += 1) {
          if (this.selectedPlan[i].partname === reportData[j].partname) {
            const payload = {
              ...this.selectedPlan[i],
              ...reportData[j],
            };
            this.setPlanView({ index: i, payload });
          }
        }
      }
    }
    this.loading = false;
  },
  methods: {
    ...mapMutations('assemblyPlanningDover', ['setPlanView']),
    ...mapActions('assemblyPlanningDover', ['getReportData', 'fetchPartMatrix']),
    getPlanStatus(status) {
      return this.planStatus(status);
    },
    getFormattedDate(datetime) {
      return formatDate(
        new Date(datetime),
        'MMM d, HH:mm',
        { locale: this.locale },
      );
    },
  },
};
</script>
